/**
 * Interaction-sound taxonomy.
 *
 * A control declares WHAT KIND of interaction it is; the cue is derived here, in
 * one place, never sniffed from the DOM. Design-system components inherit a kind
 * (Button -> "action", Switch -> "toggle") and callers override it with a
 * `soundKind` prop where the semantics differ (a modal's close is "dismiss", a
 * tab is "select"). Raw one-off controls opt in by spreading
 * `interactionSoundProps(kind)`.
 *
 * The cue is played by the host app's cuelume binding, which reads the
 * `data-cuelume-*` attribute this emits and is gated by the app-wide sound mute.
 * cuelume stays hidden behind this seam: callers speak kinds, not sound names,
 * and `packages/ui` gains no dependency (these are plain attribute strings).
 *
 * To retune a cue, change it HERE and every control of that kind follows.
 */

export type InteractionSoundKind =
  /** A button or actionable control. A soft tactile press on pointer-down. */
  | "action"
  /** Choosing among options: a tab, a segment, a platform pill. */
  | "select"
  /** Flipping a boolean: a switch. */
  | "toggle"
  /** A disclosure opening to its full self: a card unfolding. */
  | "expand"
  /** A disclosure settling closed. */
  | "collapse"
  /** A navigation disclosure opening its child links: a sidebar submenu. */
  | "reveal"
  /** Closing or cancelling: a modal X, an overlay, a "maybe later". */
  | "dismiss"
  /** Completing something meaningful: a check-in submitted. */
  | "commit"
  /** An operation failed. */
  | "fail"
  /** Explicitly silent (a control that owns its own audio, or should stay quiet). */
  | "none";

/** The attributes a control spreads to declare its cue. Opaque to callers. */
export type InteractionSoundProps = Record<string, string>;

// "action" plays on pointer-down for immediate tactile feedback; everything
// else plays on click, once the interaction commits. Encoded as the two cuelume
// delegation attributes the host app's bind() listens for.
const onPress = (sound: string): InteractionSoundProps => ({ "data-cuelume-press": sound });
const onClick = (sound: string): InteractionSoundProps => ({ "data-cuelume-toggle": sound });

const TAXONOMY: Record<InteractionSoundKind, InteractionSoundProps> = {
  action: onPress("press"),
  select: onClick("tick"),
  toggle: onClick("toggle"),
  expand: onClick("bloom"),
  collapse: onClick("whisper"),
  reveal: onClick("sparkle"),
  dismiss: onClick("whisper"),
  commit: onClick("success"),
  fail: onClick("error"),
  // "none" must actively opt OUT, not just omit a cue: the host app's global
  // default applies a press to any actionable control that has not declared
  // otherwise, so a silent control marks itself with `data-silent` or it would
  // still press (e.g. a `soundKind="none"` / `silent` / `unstyled` button).
  none: { "data-silent": "" },
};

/** Attributes that make a control sound the cue for its interaction kind. */
export function interactionSoundProps(kind: InteractionSoundKind): InteractionSoundProps {
  return TAXONOMY[kind];
}
